import React, { useState, useMemo } from 'react';
import { FiAlertCircle as AlertCircle, FiCheckCircle as CheckCircle, FiCode as Code } from 'react-icons/fi';
import { parseTemplate } from '../../../../../utils/templateParser';

interface PromptEditorProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  placeholder?: string;
  showVariableInfo?: boolean;
}

const PromptEditor: React.FC<PromptEditorProps> = ({
  value,
  onChange,
  label = 'Template',
  placeholder = 'Enter your prompt template...',
  showVariableInfo = true,
}) => {
  const [isFocused, setIsFocused] = useState(false);

  const templateMeta = useMemo(() => parseTemplate(value || ''), [value]);

  return (
    <div className="space-y-2">
      {label && (
        <span className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          {label}
          {templateMeta.variableCount > 0 && (
            <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
              ({templateMeta.variableCount} variable{templateMeta.variableCount === 1 ? '' : 's'})
            </span>
          )}
        </span>
      )}

      <div className={`relative border rounded-lg transition-colors ${
        isFocused
          ? 'border-blue-500 dark:border-blue-400 ring-2 ring-blue-200 dark:ring-blue-900'
          : 'border-gray-300 dark:border-gray-600'
      }`}>
        <textarea
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          placeholder={placeholder}
          className="w-full px-4 py-3 font-mono text-sm bg-transparent text-gray-900 dark:text-gray-100
                   focus:outline-none resize-none min-h-[200px]"
          spellCheck={false}
        />
      </div>

      {showVariableInfo && templateMeta.variableCount > 0 && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
          <div className="flex items-start gap-2">
            <Code className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-2">
                Detected Variables
              </p>
              <div className="flex flex-wrap gap-2">
                {templateMeta.variables.map((variable: { name: string; isValid: boolean }) => (
                  <div
                    key={variable.name}
                    className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-mono ${
                      variable.isValid
                        ? 'bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-100'
                        : 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-100'
                    }`}
                  >
                    {variable.isValid ? (
                      <CheckCircle className="w-3 h-3" />
                    ) : (
                      <AlertCircle className="w-3 h-3" />
                    )}
                    {`{{${variable.name}}}`}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {templateMeta.hasErrors && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-red-900 dark:text-red-100 mb-1">
                Template Errors
              </p>
              <ul className="text-sm text-red-800 dark:text-red-200 space-y-1">
                {templateMeta.errors.map((error: { message: string }) => (
                  <li key={error.message}>{error.message}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      <p className="text-xs text-gray-500 dark:text-gray-400">
        Use <code className="px-1 py-0.5 bg-gray-100 dark:bg-gray-800 rounded">{'{{variable_name}}'}</code> syntax for template variables
      </p>
    </div>
  );
};

export default PromptEditor;
